/**
 * Scientific Human Intelligence Scale & Historical Benchmarks
 * Sourced from internationally recognized WAIS-IV / Stanford-Binet standard distributions (Mean = 100, SD = 15)
 * and Stanford historical biographical psychometric studies (Dr. Catharine Cox Miles).
 */

export const HUMAN_IQ_TIERS = [
  {
    id: 'very_superior',
    range: '130+',
    minScore: 88,
    name_ar: 'موهبة فكرية فائقة (عباقرة وموهوبون)',
    name_en: 'Very Superior / Gifted (Mensa Threshold)',
    percent: '2.2',
    barPct: 100,
    color: '#8b5cf6',
    isAverage: false
  },
  {
    id: 'superior',
    range: '120 - 129',
    minScore: 75,
    name_ar: 'ذكاء متفوق جداً',
    name_en: 'Superior Intelligence',
    percent: '6.7',
    barPct: 82,
    color: '#3b82f6',
    isAverage: false
  },
  {
    id: 'high_average',
    range: '110 - 119',
    minScore: 60,
    name_ar: 'فوق المتوسط المرتفع',
    name_en: 'High Average',
    percent: '16.1',
    barPct: 65,
    color: '#38bdf8',
    isAverage: false
  },
  {
    id: 'average',
    range: '90 - 109',
    minScore: 40,
    name_ar: 'متوسط عامة البشر (المعدل الطبيعي العام)',
    name_en: 'Average / Normal Human Range (Median = 100)',
    percent: '50.0',
    barPct: 50,
    color: '#10b981',
    isAverage: true
  },
  {
    id: 'low_average',
    range: '80 - 89',
    minScore: 25,
    name_ar: 'متوسط منخفض',
    name_en: 'Low Average',
    percent: '16.1',
    barPct: 35,
    color: '#f59e0b',
    isAverage: false
  },
  {
    id: 'borderline',
    range: '70 - 79',
    minScore: 12,
    name_ar: 'نطاق حدودي',
    name_en: 'Borderline Range',
    percent: '6.7',
    barPct: 22,
    color: '#f97316',
    isAverage: false
  },
  {
    id: 'extremely_low',
    range: '< 70',
    minScore: 0,
    name_ar: 'نطاق الدعم المعرفي الخاص',
    name_en: 'Special Cognitive Support',
    percent: '2.2',
    barPct: 12,
    color: '#ef4444',
    isAverage: false
  }
];

export const SMARTEST_HUMANS = [
  {
    name_ar: 'تيرينس تاو',
    name_en: 'Terence Tao',
    iq: '225 - 230',
    field_ar: 'الرياضيات والتحليل التوافقي',
    field_en: 'Pure Mathematics & Analysis',
    achievement_ar: 'أستاذ الرياضيات في جامعة كاليفورنيا وأصغر فائز بميدالية فيلدز المرموقة في التاريخ.',
    achievement_en: 'UCLA Mathematics professor and youngest Fields Medalist in history.'
  },
  {
    name_ar: 'مارلين فوس سافانت',
    name_en: 'Marilyn vos Savant',
    iq: '190 - 228',
    field_ar: 'المنطق الرياضي والفلسفة',
    field_en: 'Mathematical Logic & Philosophy',
    achievement_ar: 'سُجلت رسمياً في موسوعة جينيس للأرقام القياسية كأعلى حاصل ذكاء مثبت وموثق.',
    achievement_en: 'Officially recorded in Guinness World Records for the highest recorded IQ.'
  },
  {
    name_ar: 'غاري كاسباروف',
    name_en: 'Garry Kasparov',
    iq: '190',
    field_ar: 'التفكير الاستراتيجي والشطرنج',
    field_en: 'Strategic Thinking & Chess',
    achievement_ar: 'أسطورة أبطال العالم للشطرنج وحامل أعلى تصنيف دولي مستمر لمدة 20 عاماً.',
    achievement_en: 'Legendary World Chess Champion holding the #1 world rank for two decades.'
  },
  {
    name_ar: 'ليوناردو دافنشي',
    name_en: 'Leonardo da Vinci',
    iq: '180 - 190',
    field_ar: 'الموسوعية والهندسة الميكانيكية',
    field_en: 'Polymath & Mechanical Engineering',
    achievement_ar: 'عبقري عصر النهضة ومبتكر دراسات التشريح والهيدروليكا وتصاميم الطيران المبكرة.',
    achievement_en: 'Renaissance polymath pioneering anatomy, hydraulics, and flight concepts.'
  },
  {
    name_ar: 'ماري كوري',
    name_en: 'Marie Curie',
    iq: '180 - 185',
    field_ar: 'الفيزياء الإشعاعية والكيمياء',
    field_en: 'Radiation Physics & Chemistry',
    achievement_ar: 'العالمة الوحيدة الحائزة على جائزتي نوبل في فرعين علميين مختلفين (الفيزياء والكيمياء).',
    achievement_en: 'The only scientist to win Nobel Prizes in two distinct scientific fields.'
  },
  {
    name_ar: 'نيكولا تيسلا',
    name_en: 'Nikola Tesla',
    iq: '160 - 180',
    field_ar: 'الكهرومغناطيسية والابتكار الفيزيائي',
    field_en: 'Electromagnetism & Invention',
    achievement_ar: 'مخترع نظام التيار المتردد والمحركات الحثية والراديو ونظم نقل الطاقة الحديثة.',
    achievement_en: 'Inventor of AC electricity, induction motors, and wireless energy transmission.'
  },
  {
    name_ar: 'ألبرت أينشتاين',
    name_en: 'Albert Einstein',
    iq: '160 - 165',
    field_ar: 'الفيزياء النظرية والنسبية',
    field_en: 'Theoretical Physics & Relativity',
    achievement_ar: 'واضع النظرية النسبية الخاصة والعامة ومغير المفاهيم الكونية حول الزمان والمكان.',
    achievement_en: 'Formulated the theory of relativity and redefined modern physics and spacetime.'
  },
  {
    name_ar: 'ستيفن هوكينغ',
    name_en: 'Stephen Hawking',
    iq: '160',
    field_ar: 'علم الكونيات والثقوب السوداء',
    field_en: 'Cosmology & Quantum Gravity',
    achievement_ar: 'مكتشف إشعاع الثقوب السوداء (إشعاع هوكينغ) وصاحب كتاب تاريخ موجز للزمان الشهير.',
    achievement_en: 'Discovered Hawking radiation and authored A Brief History of Time.'
  }
];

export function getUserIqTier(performanceIndex) {
  for (const tier of HUMAN_IQ_TIERS) {
    if (performanceIndex >= tier.minScore) {
      return tier;
    }
  }
  return HUMAN_IQ_TIERS[HUMAN_IQ_TIERS.length - 1];
}

/**
 * Maps performance index (0-100) to the internationally standardized
 * Wechsler / Stanford-Binet IQ scale (Mean = 100, SD = 15).
 * Guaranteed to align with each tier's defined range.
 */
export function calculateEstimatedIq(performanceIndex) {
  const p = Math.max(0, Math.min(100, Math.round(performanceIndex)));
  if (p >= 88) {
    return Math.round(130 + ((p - 88) / 12) * 15);
  }
  if (p >= 75) {
    return Math.round(120 + ((p - 75) / 12) * 9);
  }
  if (p >= 60) {
    return Math.round(110 + ((p - 60) / 14) * 9);
  }
  if (p >= 40) {
    return Math.round(90 + ((p - 40) / 19) * 19);
  }
  if (p >= 25) {
    return Math.round(80 + ((p - 25) / 14) * 9);
  }
  if (p >= 12) {
    return Math.round(70 + ((p - 12) / 12) * 9);
  }
  return Math.round(60 + (p / 11) * 9);
}

